import {
  Component,
  Input,
  ContentChildren,
  QueryList,
  Directive,
  TemplateRef,
  EventEmitter,
  Output,
  AfterContentChecked
} from '@angular/core';

let nextId = 0;

@Directive({selector: 'ng-template[cmp-tabTitle]'})
export class TabTitleDirective {
  constructor(public templateRef: TemplateRef<any>) {
  }
}

@Directive({selector: 'ng-template[cmp-tabContent]'})
export class TabContentDirective {
  constructor(public templateRef: TemplateRef<any>) {
  }
}

@Directive({selector: 'cmp-tab'})
export class TabDirective implements AfterContentChecked {
  /**
   * Unique tab identifier. Must be unique for the entire document for proper accessibility support.
   */
  @Input() id = `tab-${nextId++}`;
  /**
   * Simple (string only) title. Use the "TabDirective" directive for more complex use-cases.
   */
  @Input() title: string;
  /**
   * Allows toggling disabled state of a given state. Disabled tabs can't be selected.
   */
  @Input() disabled = false;

  titleTpl: TabTitleDirective | null;
  contentTpl: TabContentDirective | null;

  @ContentChildren(TabTitleDirective, {descendants: false}) titleTpls: QueryList<TabTitleDirective>;
  @ContentChildren(TabContentDirective, {descendants: false}) contentTpls: QueryList<TabContentDirective>;

  ngAfterContentChecked() {
    // We are using @ContentChildren instead of @ContentChild as in the Angular version being used
    // only @ContentChildren allows us to specify the {descendants: false} option.
    // Without {descendants: false} we are hitting bugs described in:
    // https://github.com/ng-bootstrap/ng-bootstrap/issues/2240
    this.titleTpl = this.titleTpls.first;
    this.contentTpl = this.contentTpls.first;
  }
}

/**
 * The payload of the change event fired right before the tab change
 */
export interface TabChangeEvent {
  /**
   * Id of the currently active tab
   */
  activeId: string;

  /**
   * Id of the newly selected tab
   */
  nextId: string;

  /**
   * Function that will prevent tab switch if called
   */
  preventDefault: () => void;
}

/**
 * A component that makes it easy to create tabbed interface.
 */
@Component({
  selector: 'cmp-tabset',
  template: `<div class="tab-primary">
  <ul class="nav nav-tab border-bottom" role="tablist">
    <li class="nav-item" *ngFor="let tab of tabs">
      <a [id]="tab.id" class="nav-link" [class.active]="tab.id === activeId" [class.disabled]="tab.disabled"
         href (click)="!!select(tab.id)" role="tab" [attr.tabindex]="(tab.disabled ? '-1': undefined)"
         [attr.aria-controls]="(!destroyOnHide || tab.id === activeId ? tab.id + '-panel' : null)"
         [attr.aria-expanded]="tab.id === activeId" [attr.aria-disabled]="tab.disabled">
        {{tab.title}}<ng-template [ngTemplateOutlet]="tab.titleTpl?.templateRef"></ng-template>
      </a>
    </li>
  </ul>
  <div class="tab-content mt-2 mb-4">
    <ng-template ngFor let-tab [ngForOf]="tabs">
      <div
        class="tab-pane {{tab.id === activeId ? 'active' : null}}"
        *ngIf="!destroyOnHide || tab.id === activeId"
        role="tabpanel"
        [attr.aria-labelledby]="tab.id" id="{{tab.id}}-panel"
        [attr.aria-expanded]="tab.id === activeId">
        <ng-template [ngTemplateOutlet]="tab.contentTpl?.templateRef"></ng-template>
      </div>
    </ng-template>
  </div>
</div>`,
  styles: [`.tab-primary ul li a{color:#327abd}.tab-primary ul li a.active,.tab-primary ul li a:hover{color:#2a679f;border-bottom:2px solid #327abd}`]
})
export class TabsetComponent implements AfterContentChecked {

  @ContentChildren(TabDirective) tabs: QueryList<TabDirective>;

  /**
   * An identifier of an initially selected (active) tab. Use the "select" method to switch a tab programmatically.
   */
  @Input() activeId: string;

  /**
   * Whether the closed tabs should be hidden without destroying them
   */
  @Input() destroyOnHide = true;

  /**
   * A tab change event fired right before the tab selection happens. See TabChangeEvent for payload details
   */
  @Output() tabChange = new EventEmitter<TabChangeEvent>();

  constructor() {
  }

  /**
   * Selects the tab with the given id and shows its associated pane.
   * Any other tab that was previously selected becomes unselected and its associated pane is hidden.
   */
  select(tabId: string) {
    const selectedTab = this._getTabById(tabId);
    if (selectedTab && !selectedTab.disabled && this.activeId !== selectedTab.id) {
      let defaultPrevented = false;

      this.tabChange.emit(
        {
          activeId: this.activeId,
          nextId: selectedTab.id,
          preventDefault: () => {
            defaultPrevented = true;
          }
        });

      if (!defaultPrevented) {
        this.activeId = selectedTab.id;
      }
    }
  }

  ngAfterContentChecked() {
    const activeTab = this._getTabById(this.activeId);
    this.activeId = activeTab ? activeTab.id : (this.tabs.length ? this.tabs.first.id : null);
  }

  private _getTabById(id: string): TabDirective {
    const tabsWithId: TabDirective[] = this.tabs.filter(tab => tab.id === id);
    return tabsWithId.length ? tabsWithId[0] : null;
  }
}
